import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Calendar, ArrowRight, Search, X } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=blog`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Búsqueda y filtrado en tiempo real
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      // Filtro por categoría
      if (selectedCategory !== 'all' && post.category !== selectedCategory) return false;

      // Búsqueda en tiempo real
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = post.title.toLowerCase().includes(query);
        const matchesExcerpt = post.excerpt.toLowerCase().includes(query);
        const matchesCategory = post.category.toLowerCase().includes(query);
        const matchesContent = post.content.toLowerCase().includes(query);

        if (!matchesTitle && !matchesExcerpt && !matchesCategory && !matchesContent) return false;
      }

      return true;
    });
  }, [posts, selectedCategory, searchQuery]);

  const clearFilters = () => {
    setSelectedCategory('all');
    setSearchQuery('');
  };

  return (
    <>
      <SEO
        title="Blog | Portfolio"
        description="Lee mis artículos sobre diseño gráfico, fotografía y creatividad. Consejos, inspiración y recursos."
      />

      <div className="pt-32 pb-24">
        {/* Header */}
        <section className="px-6 md:px-12 lg:px-24 mb-12" data-testid="blog-header">
          <div className="max-w-screen-2xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-sm font-sans tracking-widest uppercase text-editorial mb-4">Blog</p>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8" data-testid="blog-title">
                Pensamientos &<br />
                Reflexiones
              </h1>
            </motion.div>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mb-6"
            >
              <div className="relative max-w-2xl">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ink/40" />
                <input
                  type="text"
                  placeholder="Buscar artículos por título, contenido o categoría..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="blog-search-input"
                  className="w-full pl-12 pr-12 py-4 border border-ink/20 focus:border-editorial focus:outline-none text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-ink/40 hover:text-editorial transition-colors"
                    data-testid="clear-blog-search"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>

            {/* Category Filter */}
            {categories.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="flex flex-wrap gap-3 items-center"
              >
                <button
                  onClick={() => setSelectedCategory('all')}
                  data-testid="blog-filter-all"
                  className={`px-5 py-2 text-sm font-sans tracking-widest uppercase transition-all duration-200 ${
                    selectedCategory === 'all'
                      ? 'bg-ink text-paper'
                      : 'border border-ink text-ink hover:bg-ink hover:text-paper'
                  }`}
                >
                  Todas las Categorías
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.name)}
                    data-testid={`blog-filter-${category.name.toLowerCase()}`}
                    className={`px-5 py-2 text-sm font-sans tracking-wide transition-all duration-200 ${
                      selectedCategory === category.name
                        ? 'bg-editorial text-paper'
                        : 'border border-ink text-ink hover:bg-ink hover:text-paper'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}

                {(selectedCategory !== 'all' || searchQuery) && (
                  <button
                    onClick={clearFilters}
                    data-testid="clear-blog-filters"
                    className="text-sm font-sans tracking-widest uppercase text-editorial hover:underline ml-2"
                  >
                    Limpiar
                  </button>
                )}
              </motion.div>
            )}
          </div>
        </section>

        {/* Posts List */}
        <section className="px-6 md:px-12 lg:px-24" data-testid="blog-posts">
          <div className="max-w-screen-2xl mx-auto">
            {/* Results Count */}
            {!loading && (
              <p className="text-sm font-sans tracking-widest uppercase text-ink/60 mb-8">
                {filteredPosts.length} {filteredPosts.length === 1 ? 'artículo' : 'artículos'} encontrado
                {filteredPosts.length !== 1 ? 's' : ''}
              </p>
            )}

            {loading ? (
              <div className="text-center py-20">
                <p className="text-lg font-light text-ink/60">Cargando artículos...</p>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg font-light text-ink/60 mb-4" data-testid="no-posts-message">
                  No se encontraron artículos con los filtros seleccionados.
                </p>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 border border-ink px-6 py-3 text-sm tracking-widest font-sans uppercase hover:bg-ink hover:text-paper transition-colors duration-200"
                >
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="space-y-16">
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-8 group"
                    data-testid={`blog-post-${index}`}
                  >
                    <Link to={`/blog/${post.slug}`} className="order-2 md:order-1">
                      <div className="image-zoom-container editorial-border h-80">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover image-zoom"
                          loading="lazy"
                        />
                      </div>
                    </Link>
                    <div className="order-1 md:order-2 flex flex-col justify-center">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-xs font-sans tracking-widest uppercase text-editorial">
                          {post.category}
                        </span>
                        <span className="flex items-center gap-2 text-xs font-sans tracking-widest uppercase text-ink/60">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.date)}
                        </span>
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 group-hover:text-editorial transition-colors">
                          {post.title}
                        </h2>
                      </Link>
                      <p className="text-lg font-light leading-relaxed text-ink/70 mb-6">{post.excerpt}</p>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center gap-2 text-sm font-sans tracking-widest uppercase hover:text-editorial transition-colors group"
                      >
                        Leer Más
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;